"use client"

import { ScopeProvider, useMolecule } from "bunshi/react"
import pino from "pino"
import type { Logger } from "pino"
import { useEffect, useMemo, useRef, useState } from "react"
import { LoggerScope } from "./logger-scope"

import { BlockchainFiltersMoleculeReatom as BlockchainFiltersMolecule } from "@/app/blockchains/filters/blockchain-filters-molecule.reatom"
import { ApplicationEnvironmentMolecule } from "@/app/env/store"

/**
 * These errors are not useful for us.
 * More than that, they happen on each render, so they increase logs trafiic a lot.
 */
const ignoreErrors = [
  /.*An error occurred in the Server Components render.*/,
  /.*Invalid array length.*/, // Error always happens together with "Server Components render" error
]

export type Web3Provider = "wallet" | "email" | "global-wallet" | "unknown"

export type LoggerContext = {
  userId?: string
  userDidId?: string
  web3Address?: string
  networkId?: string
  web3Provider?: Web3Provider | string
  blockchain?: string
}

export type LogMessage = {
  level: "TRACE" | "DEBUG" | "INFO" | "WARN" | "ERROR" | "FATAL"
  appVersion: string
  environment: string
  service: string
  platform: "web"
  timestampClientUnix: number
  userAgent?: string
  userPlatform?: string
  message: string
  web3Address?: string
  networkId?: string
  web3Provider?: Web3Provider | string
  blockchain?: string
  [key: string]: unknown
}

export type LoggerApi =
  | {
      api: {
        pushEvent: (name: string, params?: Record<string, string>) => void
        pushError: (
          error: Error,
          context?: { context?: Record<string, string> },
        ) => void
        setContext: (context: Partial<LoggerContext>) => void
        resetContext: () => void
      }
    }
  | undefined

/**
 * Helper to serialize errors for logging
 */
function serializeError(error: Error): Record<string, unknown> {
  return {
    name: error.name,
    message: error.message,
    stack: error.stack,
    ...Object.getOwnPropertyNames(error).reduce(
      (acc, key) => {
        // biome-ignore lint/suspicious/noExplicitAny: <explanation>
        acc[key] = (error as any)[key]
        return acc
      },
      {} as Record<string, unknown>,
    ),
  }
}

/**
 * Custom HTTP transport for pino that batches and sends logs to a remote endpoint
 */
function createHttpTransport(
  url: string,
  environment: string,
  throttleMs = 2000,
) {
  let logBuffer: unknown[] = []
  let timeoutId: NodeJS.Timeout | null = null

  const sendLogs = async () => {
    if (logBuffer.length === 0) return

    const logsToSend = [...logBuffer]
    logBuffer = []

    try {
      await fetch(url, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(logsToSend),
        // Use keepalive for better reliability
        keepalive: true,
      })
    } catch (error) {
      // Silently fail - we don't want logging errors to break the app
      console.error("Failed to send logs:", error)
    }
  }

  const scheduleFlush = () => {
    if (timeoutId) {
      clearTimeout(timeoutId)
    }
    timeoutId = setTimeout(() => {
      sendLogs()
      timeoutId = null
    }, throttleMs)
  }

  const levelMap: Record<number, string> = {
    10: "TRACE",
    20: "DEBUG",
    30: "INFO",
    40: "WARN",
    50: "ERROR",
    60: "FATAL",
  }

  return {
    send: (
      level: string,
      logEvent: {
        ts: number
        messages: unknown[]
        bindings: unknown[]
        level: { label: string; value: number }
      },
    ) => {
      // Transform pino log event to custom format
      const firstMessage = logEvent.messages[0]
      const messageData =
        typeof firstMessage === "object" && firstMessage !== null
          ? (firstMessage as Record<string, unknown>)
          : {}

      // Merge bindings (from child logger context) with message data
      const bindingsData =
        logEvent.bindings && logEvent.bindings.length > 0
          ? (logEvent.bindings[0] as Record<string, unknown>)
          : {}

      const logData = { ...bindingsData, ...messageData }

      // Extract message from the log data or use the event name
      const message =
        (logData.message as string) ||
        (logData.event as string) ||
        (logEvent.messages[1] as string) ||
        "Log event"

      const transformedLog: LogMessage = {
        level: (levelMap[logEvent.level.value] ||
          "INFO") as LogMessage["level"],
        appVersion:
          (logData.buildVersion as string) ||
          (logData.appVersion as string) ||
          "unknown",
        environment,
        service: (logData.service as string) || "anaconda-market",
        platform: "web",
        timestampClientUnix: logEvent.ts,
        userAgent:
          typeof window !== "undefined"
            ? window.navigator.userAgent
            : undefined,
        userPlatform:
          typeof window !== "undefined" ? window.navigator.platform : undefined,
        message,
      }

      // Add web3-specific fields if available
      if (logData.web3Address) {
        transformedLog.web3Address = logData.web3Address as string
      }
      if (logData.networkId) {
        transformedLog.networkId = logData.networkId as string
      }
      if (logData.web3Provider) {
        transformedLog.web3Provider = logData.web3Provider as string
      }

      // Add blockchain if available
      if (logData.selectedBlockchain) {
        transformedLog.blockchain = logData.selectedBlockchain as string
      }

      // Include all other custom fields from logData (excluding already processed fields)
      const excludeFields = new Set([
        "buildVersion",
        "appVersion",
        "service",
        "message",
        "event",
        "web3Address",
        "networkId",
        "web3Provider",
        "selectedBlockchain",
        "name",
      ])

      for (const [key, value] of Object.entries(logData)) {
        if (!excludeFields.has(key)) {
          transformedLog[key] = value
        }
      }

      logBuffer.push(transformedLog)
      scheduleFlush()
    },
  }
}

export function LoggerScopeProvider({
  children,
}: { children: React.ReactNode }) {
  const { loggerUrl, buildVersion, hostEnvironment } = useMolecule(
    ApplicationEnvironmentMolecule,
  )

  // Track logger context in ref to avoid unnecessary re-renders
  const loggerContextRef = useRef<LoggerContext>({})

  // Create a ref to hold the child logger that includes dynamic context
  const childLoggerRef = useRef<Logger | null>(null)

  /*
  Example of log entry that should be sent to the server:
[
  {
    "level": "INFO",
    "appVersion": "0.141.69",
    "environment": "prod",
    "service": "app",
    "platform": "web",
    "web3Address": "0x0000000000000000000000000000000000000001",
    "web3Provider": "wallet" | "email" | "global-wallet" | "unknown"
    "networkId": "38",
    "blockchain": "ETHEREUM",
    "timestampUnix": 1760461905291,
    "timestampClientUnix": 1760461903565,
    "userAgent": "Mozilla/5.0 (X11; Linux x86_64) AppleWebKit/537.36 (KHTML, like Gecko) Chrome/135.0.0.0 Safari/537.36",
    "userPlatform": "Linux x86_64"
    "message": "Button clicked",
    ... other user fields
  },
]
  */

  // Initialize the base pino logger
  const baseLogger = useMemo(() => {
    if (typeof window === "undefined") {
      // Server-side: just return a dummy logger
      return null
    }

    // Create pino logger with browser configuration
    const logger = pino({
      browser: {
        asObject: true,
        transmit: loggerUrl
          ? {
              level: "info",
              send: createHttpTransport(loggerUrl, "prod", 2000).send,
            }
          : undefined,
        write: {
          // Suppress all console output
          fatal: () => {},
          error: () => {},
          warn: () => {},
          info: () => {},
          debug: () => {},
          trace: () => {},
        },
      },
      base: {
        buildVersion: buildVersion || "unknown",
        name: "anaconda market",
        service: "anaconda-market",
      },
    })

    return logger
  }, [loggerUrl, buildVersion])

  // Helper to update child logger with current context
  const updateChildLogger = () => {
    if (!baseLogger) {
      childLoggerRef.current = null
      return
    }

    const contextBindings: Record<string, string> = {
      name: "anaconda market",
      service: "anaconda-market",
      buildVersion: buildVersion || "local",
    }

    const loggerContext = loggerContextRef.current
    if (loggerContext.userId) {
      contextBindings.userId = loggerContext.userId
    }
    if (loggerContext.userDidId) {
      contextBindings.userDidId = loggerContext.userDidId
    }
    if (loggerContext.web3Address) {
      contextBindings.web3Address = loggerContext.web3Address
    }
    if (loggerContext.networkId) {
      contextBindings.networkId = loggerContext.networkId
    }
    if (loggerContext.web3Provider) {
      contextBindings.web3Provider = loggerContext.web3Provider
    }

    childLoggerRef.current = baseLogger.child(contextBindings)
  }

  // Initialize child logger once on mount
  // biome-ignore lint/correctness/useExhaustiveDependencies: <explanation>
  useEffect(() => {
    updateChildLogger()
  }, [baseLogger, buildVersion])

  // Create the logger API
  // biome-ignore lint/correctness/useExhaustiveDependencies: <explanation>
  const logger: LoggerApi = useMemo(() => {
    if (!baseLogger) {
      // Server-side or no logger: return dummy API
      return {
        api: {
          pushEvent: () => {},
          pushError: () => {},
          setContext: () => {},
          resetContext: () => {},
        },
      }
    }

    return {
      api: {
        pushEvent(name: string, params?: Record<string, string>) {
          const currentLogger = childLoggerRef.current || baseLogger
          currentLogger.info(
            {
              event: name,
              ...params,
            },
            name,
          )
        },

        pushError(
          error: Error,
          context?: { context?: Record<string, string> },
        ) {
          const currentLogger = childLoggerRef.current || baseLogger

          // Check if error should be ignored
          const shouldIgnore = ignoreErrors.some((pattern) =>
            pattern.test(error.message),
          )

          if (shouldIgnore) {
            return
          }

          const errorData = serializeError(error)

          currentLogger.error(
            {
              error: errorData,
              ...context?.context,
            },
            error.message,
          )
        },

        setContext(context: Partial<LoggerContext>) {
          const wasWeb3AddressSet = !!loggerContextRef.current.web3Address
          const isWeb3AddressNowSet = !!context.web3Address

          // Merge new context with existing context (partial update)
          loggerContextRef.current = {
            ...loggerContextRef.current,
            ...context,
          }

          // Update child logger with new context
          updateChildLogger()

          // Log when web3Address becomes available for the first time
          if (
            !wasWeb3AddressSet &&
            isWeb3AddressNowSet &&
            childLoggerRef.current
          ) {
            childLoggerRef.current.info(
              {
                event: "Web3 wallet connected",
              },
              "Web3 wallet connected",
            )
          }
        },

        resetContext() {
          loggerContextRef.current = {}
          updateChildLogger()
        },
      },
    }
  }, [baseLogger])

  useEffect(() => {
    logger?.api.pushEvent("init app")
  }, [logger])

  return (
    <ScopeProvider scope={LoggerScope} value={logger}>
      {children}
    </ScopeProvider>
  )
}
